import { useLayoutEffect, useRef } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';

am4core.useTheme(am4themes_animated);

const yieldData = [
    { date: new Date('2019-01-01'), office: 4.8, retail: 5.2, industrial: 6.1, residential: 3.9 },
    { date: new Date('2019-07-01'), office: 4.7, retail: 5.0, industrial: 6.0, residential: 3.8 },
    { date: new Date('2020-01-01'), office: 4.5, retail: 4.6, industrial: 6.2, residential: 3.6 },
    { date: new Date('2020-07-01'), office: 4.3, retail: 4.2, industrial: 6.5, residential: 3.5 },
    { date: new Date('2021-01-01'), office: 4.4, retail: 4.4, industrial: 6.8, residential: 3.4 },
    { date: new Date('2021-07-01'), office: 4.6, retail: 4.7, industrial: 7.0, residential: 3.5 },
    { date: new Date('2022-01-01'), office: 4.9, retail: 5.0, industrial: 7.2, residential: 3.7 },
    { date: new Date('2022-07-01'), office: 5.2, retail: 5.3, industrial: 7.5, residential: 4.0 },
    { date: new Date('2023-01-01'), office: 5.4, retail: 5.5, industrial: 7.8, residential: 4.2 },
    { date: new Date('2023-07-01'), office: 5.6, retail: 5.7, industrial: 8.0, residential: 4.4 },
    { date: new Date('2024-01-01'), office: 5.5, retail: 5.6, industrial: 7.9, residential: 4.3 },
];

const rentalGrowthData = [
    { market: 'Sydney Office', value: 8.2 },
    { market: 'Singapore Office', value: 11.4 },
    { market: 'Tokyo Office', value: 4.1 },
    { market: 'HK Office', value: -2.3 },
    { market: 'Sydney Industrial', value: 18.5 },
    { market: 'Singapore Industrial', value: 14.2 },
    { market: 'Tokyo Residential', value: 3.8 },
    { market: 'HK Retail', value: -5.1 },
];

const IncomeInformation = () => {
    const yieldRef = useRef<any>(null);
    const growthRef = useRef<any>(null);

    useLayoutEffect(() => {
        const chart = am4core.create('income-yield-chart', am4charts.XYChart);
        yieldRef.current = chart;

        chart.data = yieldData;
        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';
        chart.dateFormatter.dateFormat = 'MMM yyyy';

        const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        dateAxis.title.text = '';

        const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.title.text = 'Net Yield (%)';
        valueAxis.min = 2;
        valueAxis.max = 10;

        const series: { field: string; label: string; color: string }[] = [
            { field: 'office', label: 'Office', color: '#1D4FA3' },
            { field: 'retail', label: 'Retail', color: '#D6342F' },
            { field: 'industrial', label: 'Industrial', color: '#2E8B57' },
            { field: 'residential', label: 'Residential', color: '#FF8C00' },
        ];

        series.forEach(({ field, label, color }) => {
            const s = chart.series.push(new am4charts.LineSeries());
            s.dataFields.valueY = field;
            s.dataFields.dateX = 'date';
            s.name = label;
            s.strokeWidth = 2;
            s.stroke = am4core.color(color);
            s.tooltipText = `${label}: {valueY}%`;
            s.bullets.push(new am4charts.CircleBullet());
        });

        chart.cursor = new am4charts.XYCursor();

        return () => { chart.dispose(); };
    }, []);

    useLayoutEffect(() => {
        const chart = am4core.create('income-growth-chart', am4charts.XYChart);
        growthRef.current = chart;

        chart.data = rentalGrowthData;

        const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = 'market';
        categoryAxis.renderer.labels.template.rotation = -30;
        categoryAxis.renderer.labels.template.fontSize = 11;

        const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.title.text = 'YoY Rental Growth (%)';

        const series = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.valueY = 'value';
        series.dataFields.categoryX = 'market';
        series.name = 'Rental Growth';
        series.columns.template.tooltipText = '{categoryX}: {valueY}%';
        series.columns.template.adapter.add('fill', (fill, target: any) => {
            const val = target.dataItem?.valueY ?? 0;
            return val >= 0 ? am4core.color('#1D4FA3') : am4core.color('#D6342F');
        });
        series.columns.template.adapter.add('stroke', (stroke, target: any) => {
            const val = target.dataItem?.valueY ?? 0;
            return val >= 0 ? am4core.color('#1D4FA3') : am4core.color('#D6342F');
        });

        chart.cursor = new am4charts.XYCursor();

        return () => { chart.dispose(); };
    }, []);

    return (
        <div style={{ padding: '24px 0' }}>
            <div style={{ padding: '0 30px', marginBottom: 32 }}>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1c4050', marginBottom: 8 }}>
                    APAC Commercial Real Estate — Income Information
                </h2>
                <p style={{ color: '#6e7189', fontSize: 14, maxWidth: 720 }}>
                    Net yield trends and rental growth across APAC property sectors.
                    Yields reflect prime assets; rental growth is year-on-year for 2024.
                </p>
            </div>

            <div style={{ padding: '0 30px', marginBottom: 40 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4, color: '#1c4050' }}>
                    Net Yield by Sector (%) — 2019 to 2024
                </h3>
                <div id="income-yield-chart" style={{ width: '100%', height: 380 }} />
                <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                    Source: Ascend Invest Research, JLL, CBRE (Sample Data)
                </p>
            </div>

            <div style={{ padding: '0 30px' }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4, color: '#1c4050' }}>
                    Year-on-Year Rental Growth by Market (2024)
                </h3>
                <div id="income-growth-chart" style={{ width: '100%', height: 360 }} />
                <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                    Source: Ascend Invest Research, Savills (Sample Data)
                </p>
            </div>
        </div>
    );
};

export default IncomeInformation;
